import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import React, { useEffect, useMemo, useState } from "react";
import { FixedSizeList as List } from "react-window";

import { useSelector } from "react-redux";
import TrendsBDBrandsSearch from "./TrendsBDBrandsSearch/TrendsBDBrandsSearch";
import { handleTableClickPopupforBars } from "../../helpers/funcs/handleTableClickPopupforBars";
import { TrendsBreakdownQueryIds } from "../../helpers/funcs/trendsAllQueryIDs";
import { useSearchParams } from "react-router-dom";
import TrendsBreakdownPopupForBars from "../TrendsBreakdownPopup/TrendsBreakdownPopupForBars";
import widgetMetricMap, {
  funnelRatioMetricMap,
  profitAndFirstTimeDepositMetricMap,
} from "../../helpers/funcs/widgetMetricMap";
// import { useSearchParams } from "react-router-dom";
const formatHeaderLabel = (key: string): string => {
  if (!key) return "";
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
const headCells = (metricKey: string): [string, string][] => [
  ["brand_id", "Brand ID"],
  ["brand_name", "Brand Name"],
  [metricKey || "", formatHeaderLabel(metricKey) || ""],
  ["clicks_out", "Clicks"],
];

const ROW_HEIGHT = 53; // Adjust based on your row height
const HEADER_HEIGHT = 56; // Adjust based on your header height
const COLUMN_WIDTHS = ["25%", "35%", "20%", "20%"];
const COLUMN_ALIGNMENTS = ["left", "left", "center", "center"];
// Custom row component for react-window
const TableRowComponent = React.memo(({ index, style, data }: any) => {
  const {
    rows,
    setPopupOpen,
    setSelectedTableDataBrands,
    searchParams,
    setSearchParams,
    metricKey,
    headCells,
  } = data;
  const row = rows[index];

  if (row.__isPlaceholder) {
    return (
      <div style={style}>
        <TableRow sx={{ display: "flex", height: ROW_HEIGHT }}>
          <TableCell
            colSpan={headCells.length}
            align="center"
            sx={{
              flex: "1 1 auto",
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              height: ROW_HEIGHT,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No data available
            </Typography>
          </TableCell>
        </TableRow>
      </div>
    );
  }

  return (
    <div style={style}>
      <TableRow
        sx={{
          display: "flex",
          alignItems: "center",
          height: ROW_HEIGHT,
          minHeight: ROW_HEIGHT,
          "& > *": {
            borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
          },
        }}
      >
        {headCells.map(([key]: [string, any], cellIndex: number) => (
          <TableCell
            key={cellIndex}
            align="left"
            sx={{
              width: COLUMN_WIDTHS[cellIndex],
              minWidth: COLUMN_WIDTHS[cellIndex],
              maxWidth: COLUMN_WIDTHS[cellIndex],
              height: ROW_HEIGHT,
              minHeight: ROW_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent:
                COLUMN_ALIGNMENTS[cellIndex] === "center"
                  ? "center"
                  : COLUMN_ALIGNMENTS[cellIndex] === "right"
                  ? "flex-end"
                  : "flex-start",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              padding: "0 16px", // Consistent padding with header
              boxSizing: "border-box",
              cursor:
                key === "brand_name"
                  ? data.isLoadingBDAds || data.isChildrenAPILoadingBDAds
                    ? "wait"
                    : "pointer"
                  : "default",
              color: key === "brand_name" ? "#9c27b0" : "inherit",
              ...(key === "brand_name" && {
                transition: "all 0.1s ease-in-out",
                "&:hover": {
                  color: "#9c27b0",
                  fontSize: "0.90rem",
                  transform: "scale(1.00)",
                  fontWeight: "500",
                },
              }),
            }}
            onClick={
              key === "brand_name"
                ? () => {
                    if (data.isLoadingBDAds || data.isChildrenAPILoadingBDAds)
                      return;
                    handleTableClickPopupforBars({
                      keywordName: row.brand_name,
                      keywordId: row.brand_id,
                      setSelectedBarData: setSelectedTableDataBrands,
                      setPopupOpen,
                      searchParams,
                      setSearchParams,
                      section: "Brands Table",
                      queryID: TrendsBreakdownQueryIds.BRANDS,
                    });
                  }
                : undefined
            }
            data-tracker="492"
          >
            {key === metricKey && row[key] != null
              ? parseFloat(row[key]).toFixed(2)
              : row[key] ?? ""}
          </TableCell>
        ))}
      </TableRow>
    </div>
  );
});

TableRowComponent.displayName = "TableRowComponent";

const TrendsBDBrandsTable = () => {
  const {
    brandsDataTrendsBreakdown,
    selectedSearchBrand,
    isLoadingBDAds,
    isChildrenAPILoadingBDAds,
  } = useSelector((state: any) => state.trends);
  const [orderBy, setOrderBy] = useState<string>("clicks_out"); // default sort column
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("desc");
  const [searchParams, setSearchParams] = useSearchParams();
  const [popupOpen, setPopupOpen] = useState(false);
  const fullWidgetName: any = searchParams.get("full_widget_name");
  const metricName: any = searchParams.get("metric_name");
  const [selectedTableDataBrands, setSelectedTableDataBrands] = useState(null);
  const allRows = useMemo(
    () => brandsDataTrendsBreakdown?.data?.data?.query_output || [],
    [brandsDataTrendsBreakdown]
  );
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderBy(property);
    setOrderDirection(isAsc ? "desc" : "asc");
  };

  const rows = useMemo(() => {
    let filtered = [...allRows];

    if (selectedSearchBrand) {
      filtered = filtered.filter((row) =>
        row.brand_name
          ?.toLowerCase()
          .includes(selectedSearchBrand.toLowerCase())
      );
    }

    filtered.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return orderDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return orderDirection === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    if (filtered.length === 0) {
      return [
        {
          __isPlaceholder: true, // special flag
        },
      ];
    }

    return filtered;
  }, [allRows, orderBy, orderDirection, selectedSearchBrand]);

  // Calculate container height (max 588px as in original)
  const containerHeight = Math.min(
    588,
    HEADER_HEIGHT + rows.length * ROW_HEIGHT
  );
  const listHeight = containerHeight - HEADER_HEIGHT;
  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedTableDataBrands(null);
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("section");
    updatedParams.delete("queryID");
    setSearchParams(updatedParams);
  };

  useEffect(() => {
    const section = searchParams.get("section");
    const queryID = searchParams.get("queryID");

    if (section || queryID) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete("section");
      updatedParams.delete("queryID");
      setSearchParams(updatedParams, { replace: true }); // avoid history entry
    }
    // eslint-disable-next-line
  }, []);
  const metricKey =
    fullWidgetName === "Funnel Ratio"
      ? funnelRatioMetricMap[metricName] || ""
      : fullWidgetName === "Profit and First Time Deposit"
      ? profitAndFirstTimeDepositMetricMap[metricName] || ""
      : widgetMetricMap[fullWidgetName];
  const effectiveMetricKey = metricKey === "epc" ? "epc_out" : metricKey;
  const dynamicHeadCells = headCells(effectiveMetricKey);
  return (
    <>
      <Paper
        sx={{
          boxShadow: "none",
          padding: "8px 16px 16px 16px",
          borderEndEndRadius: "0px",
          borderBottomLeftRadius: "0px",
        }}
      >
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h3" sx={{ fontSize: "24px" }}>
            Breakdown By Brands
          </Typography>
          <TrendsBDBrandsSearch />
        </Stack>

        <TableContainer
          component={Paper}
          className="PageScantablewrap"
          sx={{
            height: containerHeight,
            overflow: "hidden", // Prevent double scrollbars
          }}
        >
          <Table
            aria-label="collapsible table"
            size="small"
            sx={{ width: "100%" }}
          >
            {/* Always render the header */}
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "white",
              }}
            >
              <TableRow sx={{ display: "flex" }}>
                {dynamicHeadCells.map(([key, label], index) => (
                  <TableCell
                    key={index}
                    align="left"
                    sx={{
                      width: COLUMN_WIDTHS[index],
                      minWidth: COLUMN_WIDTHS[index],
                      maxWidth: COLUMN_WIDTHS[index],
                      padding: "0 16px",
                      boxSizing: "border-box",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        COLUMN_ALIGNMENTS[index] === "center"
                          ? "center"
                          : COLUMN_ALIGNMENTS[index] === "right"
                          ? "flex-end"
                          : "flex-start",
                    }}
                    onClick={() => handleRequestSort(key)}
                  >
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? orderDirection : "asc"}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>

          <List
            height={listHeight}
            width="100%"
            itemCount={rows.length}
            itemSize={ROW_HEIGHT}
            itemData={{
              rows,
              setPopupOpen,
              setSelectedTableDataBrands,
              searchParams,
              setSearchParams,
              isLoadingBDAds,
              isChildrenAPILoadingBDAds,
              metricKey: effectiveMetricKey,
              headCells: dynamicHeadCells,
            }}
            overscanCount={5}
          >
            {TableRowComponent}
          </List>
        </TableContainer>
      </Paper>
      <TrendsBreakdownPopupForBars
        open={popupOpen}
        onClose={handleClosePopup}
        data={selectedTableDataBrands}
      />
    </>
  );
};

export default TrendsBDBrandsTable;
